import { makeAutoObservable } from 'mobx';
import { GroupsApi, LoanAccountsApiResponse } from '../../api/GroupApi';
import { Group } from '../models/Group';
import {
  LoanAccount,
  LoanAccountsCreateApiRequest,
  LoanAccountsUpdateApiRequest,
} from '../models/LoanAccounts';
import { LoanAccountsApi } from '../../api/LoanAccountsApi';

export class LoanAccountsStore {
  groupLoanAccounts: Map<number, Map<number, LoanAccount>> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  toApi = (groupId: number): LoanAccount[] => {
    return [...this.groupLoanAccounts.get(groupId).values()];
  };

  parseApi = (groupId: number, apiData: LoanAccountsApiResponse[]) => {
    this.groupLoanAccounts.set(
      groupId,
      new Map(apiData.map(loan => [loan.id, LoanAccount.fromApi(loan)]))
    );
  };

  getLoanAccounts = (groupId: number): LoanAccount[] => {
    if (!this.groupLoanAccounts.has(groupId)) return [];
    return [...this.groupLoanAccounts.get(groupId).values()];
  };

  loanName = (groupId: number, loanId: number): string => {
    return this.groupLoanAccounts.get(groupId)?.get(loanId)?.name || '';
  };

  hasLoan = (groupId: number, loanId: number): boolean => {
    return this.groupLoanAccounts.get(groupId)?.has(loanId) || false;
  };

  loadLoanAccounts = (group: Group, force: boolean = false): Promise<void> => {
    if (this.groupLoanAccounts.has(group.id) && !force) {
      return Promise.resolve();
    }
    return GroupsApi.getLoanAccounts(group.id).then(
      (apiData: LoanAccountsApiResponse[]) => this.parseApi(group.id, apiData)
    );
  };

  set = (groupId: number, loan: LoanAccount) => {
    if (!this.groupLoanAccounts.has(groupId)) {
      this.groupLoanAccounts.set(groupId, new Map<number, LoanAccount>());
    }

    this.groupLoanAccounts.get(groupId).set(loan.id, loan);
  };

  save = (groupId: number) =>
    GroupsApi.setLoanAccounts(groupId, this.toApi(groupId)).then(data =>
      this.parseApi(groupId, data)
    );

  create = (
    groupId: number,
    newLoanAccount: LoanAccountsCreateApiRequest
  ): Promise<LoanAccount> =>
    LoanAccountsApi.create(groupId, newLoanAccount).then(
      (loanAccountResponse: LoanAccountsApiResponse) => {
        const loanAccount = LoanAccount.fromApi(loanAccountResponse);
        this.groupLoanAccounts.get(groupId).set(loanAccount.id, loanAccount);

        return loanAccount;
      }
    );

  update = (
    groupId: number,
    loanAccountId: number,
    updatedLoanAccount: LoanAccountsUpdateApiRequest
  ): Promise<LoanAccount> =>
    LoanAccountsApi.update(groupId, loanAccountId, updatedLoanAccount).then(
      (loanAccountResponse: LoanAccountsApiResponse) => {
        const loanAccount = LoanAccount.fromApi(loanAccountResponse);
        this.groupLoanAccounts.get(groupId).set(loanAccount.id, loanAccount);

        return loanAccount;
      }
    );

  remove = (groupId: number, loanAccountId: number): Promise<LoanAccount> =>
    LoanAccountsApi.remove(groupId, loanAccountId).then(
      (loanAccountResponse: LoanAccountsApiResponse) => {
        if (
          !this.groupLoanAccounts.get(groupId)!.delete(loanAccountResponse.id)
        )
          throw new Error(
            `Loan account with id: ${loanAccountResponse} does not exists in the store.`
          );
        return LoanAccount.fromApi(loanAccountResponse);
      }
    );

  getCodesFromEntity = (groupId: number, fromId: number): string[] => {
    const codes = this.getLoanAccounts(groupId)
      .filter(it => it.org1?.id == fromId || it.org2?.id == fromId)
      .map(it =>
        it.org1?.id == fromId ? it.org1.accountCode : it.org2.accountCode
      );
    return [...new Set(codes)];
  };

  isUsed = (groupId: number, externalId: string): boolean => {
    return this.getLoanAccounts(groupId).some(
      it =>
        it.org1?.ncExternalId === externalId ||
        it.org2?.ncExternalId === externalId
    );
  };
}
